import React, { ComponentType } from 'react';
import Logo from 'components/common/Logo';
import TrustpilotStars from 'components/common/TrustpilotStars';
import { Heading05 } from '@uds/typography';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Container } from '@uds/grid';
import PaddingBox from '@uds/padding-box';
import { useBreakpoints } from '@uds/responsive';
import errorBoundary from 'presentation/hocs/errorBoundary';
import getLocalizedTrustPilotUrl from 'presentation/utils/getTrustpilotUrl';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { useBrandConfig, useLocale } from '@ux/whitelabel';
import visibilitySet from 'presentation/hocs/visibilitySet';
import findResponsiveItem from 'presentation/utils/findResponsiveItem';
import { Alignment } from 'domain/constants/alignment';

import Ribbon from '../Ribbon';
import getClassNames from './AggregateRatingTrustpilot.stylesheet';

export interface OuterProps extends RibbonProps {
  stars: string;
  trustScore: number;
  numberOfReviews?: {
    total?: number;
    usedForTrustScoreCalculation?: number;
  };
  url: string;
  hBaseline?: number;
}

export const AggregateRatingTrustpilot = ({
  id,
  colorway,
  layout,
  loading,
  hBaseline = 1,
  stars,
  trustScore,
  name,
  numberOfReviews,
}: OuterProps) => {
  const brandConfig = useBrandConfig();
  const trustpilotPageId = brandConfig?.trustPilot?.pageId;
  const currentLanguage = useLocale();

  const url = getLocalizedTrustPilotUrl(currentLanguage, trustpilotPageId);

  const alignment = findResponsiveItem(useBreakpoints(), [
    layout?.alignmentMobile ?? Alignment.CENTER,
    layout?.alignmentTablet ?? Alignment.STARTING,
    layout?.alignment ?? Alignment.STARTING,
  ]) as Alignment;

  const classNames = getClassNames({
    alignment,
  });
  const reviewCount = numberOfReviews?.total ?? null;
  
  return (
    <Ribbon
      id={id}
      colorway={colorway}
      layout={layout}
      flush={true}
      loading={loading}
      name={name}
      type={Type.AGGREGATE_RATING_TRUST_PILOT}
    >
      <Container>
        <PaddingBox>
          <div className={classNames.root}>
            <div className={classNames.text}>
              <Heading05 className={classNames.title} depth={hBaseline}>
                <FormattedMessage
                  id="usf.ribbons.aggregateRating.trustpilot.title"
                  values={{
                    rating: (
                      <strong className={classNames.bold}>
                        <FormattedMessage
                          id="usf.ribbons.aggregateRating.trustpilot.rating"
                          values={{
                            stars: <FormattedNumber value={stars as unknown as number} />,
                            trustScore: <FormattedNumber value={trustScore} />,
                          }}
                        />
                      </strong>
                    ),
                    reviews: (
                      <strong className={classNames.bold}>
                        <FormattedMessage
                          id="usf.ribbons.aggregateRating.trustpilot.reviews"
                          values={{
                            reviews: <FormattedNumber value={reviewCount!} />,
                          }}
                        />
                      </strong>
                    ),
                  }}
                />
              </Heading05>
            </div>
            <div className={classNames.imagery}>
              <div className={classNames.logo}>
                <Logo colorway={colorway} url={url} />
              </div>
              <div className={classNames.stars}>
                <TrustpilotStars rating={stars} />
              </div>
            </div>
          </div>
        </PaddingBox>
      </Container>
    </Ribbon>
  );
};

AggregateRatingTrustpilot.displayName = 'AggregateRatingTrustpilot';

export default errorBoundary(visibilitySet(AggregateRatingTrustpilot)) as ComponentType<OuterProps>;
